import type { Params } from "../toQueryString";
import { buildPath } from "./buildPath";

interface ParamsProps {
  params?: Params;
}

interface TaskIdProps {
  params?: Params;
  taskId: string;
}

interface TemplateIdProps {
  params?: Params;
  templateId: string;
}

interface TemplateIdWithOverridesProps {
  overrides?: Params;
  params?: Params;
  templateId: string;
}

interface TemplateKeyProps {
  templateKey: string;
}

interface IdProps {
  id: string;
  params?: Params;
}

interface WorkflowIdProps {
  params?: Params;
  workflowId: string;
}

interface WorkflowIdIdProps {
  id: string;
  params?: Params;
  workflowId: string;
}

interface WorkflowIdRunIdProps {
  params?: Params;
  runId: string;
  workflowId: string;
}

interface PaywallIdIdProps {
  id: string;
  params?: Params;
  paywallId: string;
}

const basePath = "/internal_api/workflows";
const workflowsPath = `${basePath}/workflows`;

const triggerFn = ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
  buildPath(`${workflowsPath}/${workflowId}/triggers/${id}`, params);

export const workflows = {
  create: ({ params = {} }: ParamsProps) => buildPath(workflowsPath, params),
  list: ({ params = {} }: ParamsProps) => buildPath(workflowsPath, params),
  fetch: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}`, params),
  update: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}`, params),
  activate: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}/activate`, params),
  inactivate: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}/inactivate`, params),
  archive: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}/archive`, params),
  duplicate: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}/duplicate`, params),
  delete: ({ id, params = {} }: IdProps) =>
    buildPath(`${workflowsPath}/${id}`, params),
  createTrigger: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/triggers`, params),
  updateTrigger: triggerFn,
  deleteTrigger: triggerFn,
  createRun: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/runs`, params),
  fetchRuns: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/runs`, params),
  fetchRun: ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/runs/${runId}`, params),
  cancelRun: ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/runs/${runId}/cancel`, params),
  fetchTasksInRun: ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/runs/${runId}/tasks`, params),
  createAudience: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/audiences`, params),
  updateAudience: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/audiences/${id}`, params),
  createAction: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/actions`, params),
  updateAction: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/actions/${id}`, params),
  deleteAction: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/actions/${id}`, params),
  upsertActions: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/actions/upsert`, params),
  updateGraph: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/graph`, params),
  createOneTimeSchedule: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/schedule/one_time`, params),
  updateOneTimeSchedule: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/schedule/one_time/${id}`, params),
  createRecurringSchedule: ({ workflowId, params = {} }: WorkflowIdProps) =>
    buildPath(`${workflowsPath}/${workflowId}/schedule/recurring`, params),
  updateRecurringSchedule: ({
    workflowId,
    id,
    params = {},
  }: WorkflowIdIdProps) =>
    buildPath(
      `${workflowsPath}/${workflowId}/schedule/recurring/${id}`,
      params,
    ),
  reschedule: ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
    buildPath(
      `${workflowsPath}/${workflowId}/schedule/reschedule/${id}`,
      params,
    ),
  tasks: ({ params = {} }: ParamsProps) =>
    buildPath(`${basePath}/tasks`, params),
  taskDetail: ({ taskId, params = {} }: TaskIdProps) =>
    buildPath(`${basePath}/tasks/${taskId}`, params),
  templates: ({ params = {} }: ParamsProps) =>
    buildPath(`${basePath}/templates`, params),
  fetchTemplate: ({ templateId, params = {} }: TemplateIdProps) =>
    buildPath(`${basePath}/templates/${templateId}`, params),
  createFromTemplate: ({
    templateId,
    params = {},
  }: TemplateIdWithOverridesProps) =>
    buildPath(`${basePath}/templates/${templateId}/create_workflow`, params),
  createFromTemplateKey: ({ templateKey }: TemplateKeyProps) =>
    buildPath(`${basePath}/templates/${templateKey}/create_workflow`),
  fetchCommunityCurrentUsageStats: ({ params = {} }: ParamsProps) =>
    buildPath(`${basePath}/community_current_usage_stats`, params),
  fetchInterpolatedMessage: ({ params = {} }: ParamsProps) =>
    buildPath(`${basePath}/preview_messages/interpolated_message`, params),
  fetchDynamicVariables: ({ params = {} }: ParamsProps) =>
    buildPath(`${basePath}/dynamic_variables`, params),
  testWebhook: ({ params = {} }: ParamsProps) =>
    buildPath(`${basePath}/webhook_tests`, params),
  events: {
    index: ({ params = {} }: ParamsProps) =>
      buildPath("/internal_api/workflows/events", params),
    show: ({ id, params = {} }: IdProps) =>
      buildPath(`/internal_api/workflows/events/${id}`, params),
  },
  posts: {
    show: ({ id, params = {} }: IdProps) =>
      buildPath(`/internal_api/workflows/posts/${id}`, params),
  },
  recurringEventSettings: {
    show: ({ id, params = {} }: IdProps) =>
      buildPath(
        `/internal_api/workflows/recurring_event_settings/${id}`,
        params,
      ),
  },
  paywallPrices: {
    show: ({ paywallId, id, params = {} }: PaywallIdIdProps) =>
      buildPath(
        `/internal_api/workflows/paywalls/${paywallId}/prices/${id}`,
        params,
      ),
  },
  previewMessages: {
    previewAttributes: ({ params = {} }: ParamsProps) =>
      buildPath(
        "/internal_api/workflows/preview_messages/preview_attributes",
        params,
      ),
  },
  contacts: {
    index: ({ params = {} }: ParamsProps) =>
      buildPath("/internal_api/workflows/contacts", params),
  },
  broadcasts: {
    index: ({ params = {} }: ParamsProps) =>
      buildPath("/internal_api/workflows/broadcasts", params),
    show: ({ id, params = {} }: IdProps) =>
      buildPath(`/internal_api/workflows/broadcasts/${id}`, params),
    links: ({ id, params = {} }: IdProps) =>
      buildPath(`/internal_api/workflows/broadcasts/${id}/links`, params),
  },
} as const;
